import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useUser } from '@/contexts/user';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { PAGE_TOOLTIPS, TooltipDefinition, TooltipPage } from '@/data/tooltipDefinitions';
import { useTooltipAnchorRegistry } from '@/contexts/TooltipAnchorContext';

interface TooltipGuideState {
  currentTooltip: TooltipDefinition | null;
  currentIndex: number;
  totalSteps: number;
  isActive: boolean;
  currentPage: TooltipPage | null;
  anchorElement: HTMLElement | null;
  next: () => void;
  dismiss: () => void;
  skipCurrent: () => void;
}

// Tooltip dismissal is intentionally storage-free (no sessionStorage/localStorage).

// IMPROVEMENT #3: Help Mode Escape Hatch (wired, no UI yet)
const HELP_MODE_ENABLED = false;

export function useTooltipGuide(): TooltipGuideState {
  const { user, loading: userLoading } = useUser();
  const { loading: authLoading, onboardingCompleted } = useAuth();
  const location = useLocation();
  
  // Get anchor registry (see docs/invariants/LIFECYCLE_OWNERSHIP.md)
  const { anchors } = useTooltipAnchorRegistry();

  const [stepIndex, setStepIndex] = useState(0);
  
  // No persisted dismissal state: tooltips are controlled solely by first_study_done.
  // When a page's sequence is finished/dismissed, we just advance stepIndex past the end.

  // Detect current page from route
  const currentPage = useMemo((): TooltipPage | null => {
    const path = location.pathname;
    if (path === '/timer' || path === '/') return 'timer';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/profile') return 'profile';
    return null;
  }, [location.pathname]);

  // Get current tooltip sequence
  const currentSequence = useMemo(() => {
    if (!currentPage) return [];
    return PAGE_TOOLTIPS[currentPage] || [];
  }, [currentPage]);

  // Check if tooltips should be active
  const isActive = useMemo(() => {
    if (authLoading || userLoading) return false;
    if (!onboardingCompleted) return false;
    if (user.id === 'user-default-id') return false;

    // IMPROVEMENT #3: Help Mode escape hatch
    // Check for truthy value OR any study time (belt & suspenders for legacy users)
    if (!HELP_MODE_ENABLED && (user.first_study_done || user.todayStudyTime > 0)) return false;

    if (!currentPage) return false;
    if (currentSequence.length === 0) return false;

    // If the user finished/dismissed the sequence for this page, don't show again until they leave/re-enter.
    if (stepIndex >= currentSequence.length) return false;

    // Block during active session
    if (typeof window !== 'undefined') {
      const focusMode = sessionStorage.getItem('focusMode') === 'true';
      if (focusMode) return false;
      const activeSession = localStorage.getItem('activeStudySession');
      if (activeSession) return false;
    }

    return true;
  }, [authLoading, userLoading, onboardingCompleted, user.id, user.first_study_done, user.todayStudyTime, currentPage, currentSequence, stepIndex]);

  // Reset step index when page changes
  useEffect(() => {
    setStepIndex(0);
  }, [currentPage]);

  // Current tooltip data
  const currentTooltip = useMemo(() => {
    if (!isActive) return null;
    if (stepIndex >= currentSequence.length) return null;
    return currentSequence[stepIndex];
  }, [isActive, stepIndex, currentSequence]);

  // Get anchor element from registry (lifecycle-safe, no stale references)
  // See: docs/invariants/LIFECYCLE_OWNERSHIP.md
  const anchorElement = useMemo(() => {
    if (!currentTooltip) return null;
    return anchors.get(currentTooltip.anchorId) || null;
  }, [currentTooltip, anchors]);

  // Internal skip function
  const skipCurrentInternal = useCallback(() => {
    if (stepIndex < currentSequence.length - 1) {
      setStepIndex(prev => prev + 1);
    } else {
      // Finish the sequence for this page (storage-free)
      setStepIndex(currentSequence.length);
    }
  }, [stepIndex, currentSequence.length]);

  // Skip to next tooltip if anchor not registered (element not mounted)
  useEffect(() => {
    if (currentTooltip && !anchorElement) {
      // Give a small delay for late-registering anchors
      const timeout = setTimeout(() => {
        // Re-check after delay
        const anchor = anchors.get(currentTooltip.anchorId);
        if (!anchor) {
          console.log(`[TooltipGuide] Anchor "${currentTooltip.anchorId}" not registered, skipping`);
          skipCurrentInternal();
        }
      }, 600);
      
      return () => clearTimeout(timeout);
    }
  }, [currentTooltip, anchorElement, anchors, skipCurrentInternal]);

  const next = useCallback(() => {
    if (stepIndex < currentSequence.length - 1) {
      setStepIndex(prev => prev + 1);
    } else {
      // Finish the sequence for this page (storage-free)
      setStepIndex(currentSequence.length);
    }
  }, [stepIndex, currentSequence.length]);

  const dismiss = useCallback(() => {
    // Dismiss the rest of the tooltips on this page for the current visit (storage-free)
    setStepIndex(currentSequence.length);
  }, [currentSequence.length]);

  const skipCurrent = useCallback(() => {
    skipCurrentInternal();
  }, [skipCurrentInternal]);

  return {
    currentTooltip,
    currentIndex: stepIndex,
    totalSteps: currentSequence.length,
    isActive,
    currentPage,
    anchorElement,
    next,
    dismiss,
    skipCurrent,
  };
}
